import { ref } from 'vue'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { desktopOnlySteps, universalSteps, accountSteps, whatsNewV3Steps, buildWelcomeStep, buildWhatsNewStep, buildClosingStep, TOUR_VERSION } from '../tours/tourSteps'

const TOUR_VERSION_KEY = 'tour_version_seen'

const MOBILE_NAV_ID_BY_ROUTE = {
  '/': 'attendance',
  '/stats': 'month-stats',
  '/students': 'students',
  '/my-schedule': 'my-schedule',
}

// Mirrors the overlayOpacity passed into driver()'s config below — kept as a named
// constant so lowDim steps (see tourSteps.js) can restore the surface's normal dimming
// on deselect rather than hardcoding a duplicate number that could drift out of sync.
const OVERLAY_OPACITY = { mobile: 0.08, desktop: 0.6 }
const LOW_DIM_OPACITY = 0.08

// driver.js reuses the SAME overlay <path> element across every step in a tour (only
// its `d` shape attribute is updated per-step; see M()/A() in driver.js's source) — so
// setting this element's opacity directly persists step-to-step exactly like the ring
// system does, with no need to fight driver.js's own (config-level-only; there's no
// per-step option) overlayOpacity.
function setOverlayOpacity(value) {
  const path = document.querySelector('.driver-overlay path')
  if (path) path.style.opacity = value
}

const tourActive = ref(false)
let driverObj = null
let router = null
let currentSurface = null // 'mobile' | 'desktop' — surface the ACTIVE tour was built for
let contentSteps = []     // parallel array (nav metadata) for the driver steps between welcome/closing
let effectiveNavs = []    // parallel to contentSteps: { mobileRoute, desktopPane } carried forward — see applyStepNav

function toSurface(isWideVal) {
  return isWideVal ? 'desktop' : 'mobile'
}

// These modals are all Teleported to <body> and stay mounted regardless of route/pane
// changes — advancing the tour past them must explicitly close them via their own
// Close/X buttons, or they sit on top of every later step. The create-lesson modal is
// only ever opened by the user actually trying the calendar-create-lesson step for
// themselves (see the pointer-events exemption in ensureTourStyles), so it needs the
// same unconditional close-on-any-transition treatment.
const LESSON_MODAL_CLOSE_SELECTORS = [
  '[data-tour="lesson-edit-close-btn"]',
  '[data-tour="lesson-details-close-btn"]',
  '[data-tour="lesson-create-close-btn"]',
  '[data-tour="calendar-grid-choice-close-btn"]',
  '[data-tour="edit-profile-close-btn"]',
  '[data-tour="preferences-close-btn"]',
]

function closeLessonModals() {
  for (const sel of LESSON_MODAL_CLOSE_SELECTORS) {
    document.querySelector(sel)?.click()
  }
}

// Emphasis rings — a blue pulsing circle rendered at the <body> level and positioned via
// getBoundingClientRect, NOT a pseudo-element attached to the target itself, because some
// targets (e.g. the calendar zoom handle) sit inside an `overflow: hidden` ancestor used
// deliberately elsewhere to clip the calendar's rounded corners, which would clip an
// attached pseudo-element's glow too. Keyed so a "main" ring (thin drag handles, or a
// mobile nav-bar button standing in for the step's real target) and a "secondary" ring
// (e.g. a desktop tab selector, alongside driver.js's own highlight on the tab's content)
// can coexist without clobbering each other.
const emphasisRings = new Map() // key -> { el, cleanup }
// Elements the tour deliberately lets the user keep interacting with — driver.js's
// default (confirmed in its own shipped CSS: `.driver-active *{pointer-events:none}`,
// with only the active element's own subtree and the popover excepted) makes EVERYTHING
// else on the page inert while a tour is running. That's fine for most steps, but for a
// handful we want real interaction to keep working even though they're not the "active"
// (highlighted) element for that step.
//
// `includeChildren: false` for the calendar targets is deliberate, not an oversight:
// DayTimeGrid's onGridClick only fires create-lesson when `e.target === e.currentTarget`
// (its own root, not a bubbled child) — the "outside availability" shading and hour-line
// dividers are pointer-events:none IN THE REAL APP specifically so clicks pass through to
// that root. Force-including `* ` here would make those overlays themselves the click
// target instead, silently breaking "click anywhere, even outside availability" (this
// happened — confirmed by testing). The scroll container and grid root still need to be
// exempted themselves (for scroll/click hit-testing to reach them at all), just not their
// children. Invoices/the create-modal are different — their own rows/inputs/buttons DO
// need to stay genuinely interactive, so those include children.
//
// `needsOverflowAuto` is separate from (and much narrower than) the pointer-events
// exemption above — it's only for the ONE element that's actually meant to scroll.
// `calendar-viewport` (DayView.vue's scrollContainer) is that element; `day-time-grid`
// is NOT — it's rendered once PER TEACHER COLUMN (`v-for="col in day.cols"`), all
// sharing the same data-tour value, so forcing overflow:auto on that selector puts a
// scrollbar on every individual column instead of the one shared viewport (confirmed
// bug — visible as a stray scrollbar-shaped sliver on the right edge of each column).
// `armGated: true` entries are only exempted from the pointer-events lockdown while
// <body> also carries the 'tour-calendar-armed' class — see armUntilFirstClick below.
// Everything reachable through the calendar is gated (repeatedly clicking empty spots
// or the chooser it opens can keep stacking real app state up behind the coachmark);
// invoices-list/lesson-create-modal are NOT gated — the create-lesson modal's own
// Close button in particular must keep working even after arming ends, or the user has
// no way back out of whatever they opened before disarming kicked in.
const TOUR_INTERACTIVE_TARGETS = [
  { selector: '[data-tour="calendar-viewport"]', includeChildren: false, needsOverflowAuto: true, armGated: true },
  { selector: '[data-tour="day-time-grid"]', includeChildren: false, armGated: true },
  { selector: '[data-tour="invoices-list"]', includeChildren: true },
  { selector: '[data-tour="lesson-create-modal"]', includeChildren: true },
  // The empty-space tap now opens a "New Lesson" / "Mark Unavailable" chooser instead
  // of creating a lesson directly (see DayTimeGrid.vue's onGridClick) — its own buttons
  // need to stay clickable too, not just the calendar-empty-slot marker that falls
  // through to open it.
  { selector: '[data-tour="calendar-grid-choice"]', includeChildren: true, armGated: true },
]

// Cleanup for the current step's advanceOnClick listener (see tourSteps.js), if any —
// at most one is ever active at a time, torn down in onDeselected before the next
// step's onHighlightStarted might set a new one.
let advanceClickCleanup = null

// Cleanup for the current step's armUntilFirstClick listener (see tourSteps.js), if any.
let armClickCleanup = null

let stylesInjected = false

function ensureTourStyles() {
  if (stylesInjected) return
  stylesInjected = true
  const style = document.createElement('style')
  const toSelectors = (prefix) => TOUR_INTERACTIVE_TARGETS
    .filter((t) => !!t.armGated === (prefix !== ''))
    .flatMap(({ selector, includeChildren }) =>
      includeChildren ? [`${prefix}.driver-active ${selector}`, `${prefix}.driver-active ${selector} *`] : [`${prefix}.driver-active ${selector}`]
    )
  // Gated entries only get the pointer-events exemption while <body> also carries
  // 'tour-calendar-armed' (see armUntilFirstClick) — ungated ones (invoices, the
  // create-lesson modal) always get it, same as before this split.
  const interactiveSelectors = [...toSelectors('body.tour-calendar-armed '), ...toSelectors('')].join(',\n')
  style.textContent = `
.tour-emphasis-ring {
  position: fixed;
  pointer-events: none;
  z-index: 10001;
  border-radius: 9999px;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.9), 0 0 16px 4px rgba(59, 130, 246, 0.6);
  animation: tour-handle-glow 1.2s ease-in-out infinite;
}
@keyframes tour-handle-glow {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}
${interactiveSelectors} {
  pointer-events: auto !important;
}
${TOUR_INTERACTIVE_TARGETS.filter(t => t.needsOverflowAuto).map(({ selector }) => `.driver-active ${selector}`).join(', ')} {
  overflow: auto !important;
}
/* actions-menu/user-menu-intro spotlight the WHOLE expanded menu (wrapper = trigger +
   dropdown together, see tourSteps.js), not just the trigger — driver.js's own default
   exempts an active element's entire subtree from the pointer-events:none lockdown, which
   would make every real item inside (Admin, Team Calendar, Logout, ...) genuinely
   clickable and able to navigate away mid-tour. Force the dropdown CONTENT back to inert;
   nav.simulateClick/dispatchEvent (see applyStepNav) use direct DOM calls, which bypass
   pointer-events entirely, so the preferences-intro step's simulated click still works
   fine through this. */
.driver-active [data-tour="actions-menu-dropdown"] *,
.driver-active [data-tour="user-menu-dropdown"] * {
  pointer-events: none !important;
}
/* driver.js sets pointer-events:auto INLINE on the overlay's own <path> (that's how it
   detects clicks on the dimmed area) — inline styles beat stylesheet rules unless we use
   !important, so without this override that path physically intercepts every tap/scroll
   anywhere on screen, before it ever reaches the real page underneath — regardless of
   the pointer-events exemptions above. Neutralizing it here is what actually lets taps
   and scroll gestures reach calendar-viewport/invoices-list/lesson-create-modal (and
   makes overlayClickBehavior moot, which is fine — we already set it to a no-op). */
.driver-overlay path {
  pointer-events: none !important;
}
.bma-tour-popover .driver-popover-next-btn,
.bma-tour-popover .driver-popover-done-btn {
  background-color: #2563eb;
  color: #fff;
  border: none;
  /* Same border-radius as the (unstyled) Previous button's driver.js default — only
     the fill color/border should differ between the primary and secondary buttons. */
  border-radius: 3px;
  padding: 6px 14px;
  font-weight: 500;
  font-size: 13px;
}
.bma-tour-popover .driver-popover-next-btn:hover,
.bma-tour-popover .driver-popover-done-btn:hover {
  background-color: #1d4ed8;
}`
  document.head.appendChild(style)
}

function positionEmphasisRing(el, target) {
  const rect = target.getBoundingClientRect()
  el.style.top = `${rect.top - 6}px`
  el.style.left = `${rect.left - 6}px`
  el.style.width = `${rect.width + 12}px`
  el.style.height = `${rect.height + 12}px`
}

function startEmphasis(key, target) {
  stopEmphasis(key)
  if (!target) return
  ensureTourStyles()
  const el = document.createElement('div')
  el.className = 'tour-emphasis-ring'
  document.body.appendChild(el)
  // Per-frame reposition rather than scroll/resize listeners — a pane-resize handle
  // moves as the user actually drags it (a live layout change, not a scroll or window
  // resize), so the ring needs to track it continuously to visibly "follow" the drag.
  let rafId
  const tick = () => {
    positionEmphasisRing(el, target)
    rafId = requestAnimationFrame(tick)
  }
  tick()
  emphasisRings.set(key, {
    el,
    cleanup: () => cancelAnimationFrame(rafId),
  })
}

function stopEmphasis(key) {
  const entry = emphasisRings.get(key)
  if (!entry) return
  entry.cleanup()
  entry.el.remove()
  emphasisRings.delete(key)
}

function stopAllEmphasis() {
  for (const key of [...emphasisRings.keys()]) stopEmphasis(key)
}

// Synthetic "empty calendar slot" marker for the calendar-create-lesson step. There's no
// real, always-present, appropriately-sized element to point at (the grid is a blank
// canvas with absolutely-positioned lesson cards, not discrete clickable slots), so this
// is a body-appended, pointer-events:none div we position ourselves — sized so the
// emphasis ring around it (which pads out +6px per side, see positionEmphasisRing) lands
// on an 80px-diameter circle. pointer-events:none means a real tap at that screen
// position falls through to the actual calendar grid underneath, opening the real
// create-lesson modal.
let calendarSlotEl = null
let calendarSlotRaf = null

// Recomputed on every tick (not just once when the step starts) — a dead-center-of-
// viewport spot was fine when first placed, but if the user scrolls the calendar while
// this step is active (nothing stops them; the tour deliberately allows it), whatever
// was empty at placement time can scroll a lesson card underneath it, making the target
// look broken ("why is it telling me to tap on Jane's lesson?"). Re-scanning every
// frame means it tracks scrolling live instead of freezing a stale answer. This step's
// overlay dimming is already set to LOW_DIM_OPACITY (see tourSteps.js/lowDim), so the
// marker visibly moving as the user scrolls doesn't fight a heavy dark backdrop.
function positionCalendarSlotMarker() {
  if (!calendarSlotEl) return
  const viewportEl = document.querySelector('[data-tour="calendar-viewport"]')
  if (!viewportEl) return
  const viewRect = viewportEl.getBoundingClientRect()
  if (viewRect.width === 0 || viewRect.height === 0) return
  const size = 68 // + the ring's 6px-per-side padding = 80px diameter

  // The sticky time gutter / hour-height-handle occupies the same on-screen rect as the
  // header row + left gutter regardless of scroll (it's itself sticky) — using its live
  // rect to inset the search area is simpler and more robust than hardcoding
  // TIME_COL_WIDTH or a header height, and avoids ever placing the marker behind either.
  const handleEl = document.querySelector('[data-tour="hour-height-handle"]')
  const handleRect = handleEl?.getBoundingClientRect()
  const searchLeft = Math.max(viewRect.left, handleRect?.right ?? viewRect.left)
  const searchTop = Math.max(viewRect.top, handleRect?.bottom ?? viewRect.top)

  const occupiedRects = [...document.querySelectorAll('[data-tour="lesson-slot"]')].map(el => el.getBoundingClientRect())
  const margin = 4 // keep clear of a card's edge, not just its exact bounding box
  const isFree = (x, y) =>
    x >= searchLeft && x + size <= viewRect.right &&
    y >= searchTop && y + size <= viewRect.bottom &&
    !occupiedRects.some(r =>
      x < r.right + margin && x + size > r.left - margin &&
      y < r.bottom + margin && y + size > r.top - margin
    )

  let spot = null
  const step = 24
  for (const grid of document.querySelectorAll('[data-tour="day-time-grid"]')) {
    const gRect = grid.getBoundingClientRect()
    if (gRect.width === 0 || gRect.height === 0) continue
    const x = gRect.left + gRect.width / 2 - size / 2
    if (x < searchLeft || x + size > viewRect.right) continue // column not (fully) visible
    const top = Math.max(gRect.top, searchTop)
    const bottom = Math.min(gRect.bottom, viewRect.bottom)
    for (let y = top; y + size <= bottom; y += step) {
      if (isFree(x, y)) { spot = { x, y }; break }
    }
    if (spot) break
  }

  // Fallback: dead center of the viewport — only reached if every visible column is
  // fully packed with lessons (or the grid hasn't mounted yet).
  if (!spot) {
    spot = {
      x: viewRect.left + viewRect.width / 2 - size / 2,
      y: viewRect.top + viewRect.height / 2 - size / 2,
    }
  }

  calendarSlotEl.style.left = `${spot.x}px`
  calendarSlotEl.style.top = `${spot.y}px`
  calendarSlotEl.style.width = `${size}px`
  calendarSlotEl.style.height = `${size}px`
}

function startCalendarSlotTracking() {
  stopCalendarSlotTracking()
  calendarSlotEl = document.createElement('div')
  calendarSlotEl.setAttribute('data-tour', 'calendar-empty-slot')
  calendarSlotEl.style.position = 'fixed'
  calendarSlotEl.style.pointerEvents = 'none'
  document.body.appendChild(calendarSlotEl)
  const tick = () => {
    positionCalendarSlotMarker()
    calendarSlotRaf = requestAnimationFrame(tick)
  }
  tick()
}

function stopCalendarSlotTracking() {
  if (calendarSlotRaf) cancelAnimationFrame(calendarSlotRaf)
  calendarSlotRaf = null
  calendarSlotEl?.remove()
  calendarSlotEl = null
}

// Resolves a field that may be a plain value (same on both surfaces) or a
// `{ mobile, desktop }` object (surface-specific — either key may be absent, meaning
// "nothing" on that surface, e.g. no element at all → a centered floating popover).
function bySurface(value, surface) {
  if (value && typeof value === 'object' && !Array.isArray(value) && ('mobile' in value || 'desktop' in value)) {
    return value[surface]
  }
  return value
}

function shouldEmphasizeMain(s, surface) {
  if (s.emphasize === 'mobile') return surface === 'mobile'
  if (s.emphasize === 'desktop') return surface === 'desktop'
  return !!s.emphasize
}

// Applies the nav needed to display contentSteps[index], by CONTENT INDEX rather than
// step object — so it can recurse to a neighboring index (see keepModalsOpen below) and
// look up the precomputed effective route/pane for that index.
//
// Several steps have no `nav.mobileRoute`/`desktopPane` of their own at all (the calendar
// zoom handle, "Add a lesson") because they assume an EARLIER step already switched to
// the right tab/pane. That's true going forward one step at a time, but breaks on any
// jump — Previous, or landing directly on a step — since nothing re-establishes it.
// `effectiveNavs[index]` (computed once in buildSteps) carries the most recent explicit
// route/pane forward across those gaps, so applying ANY step's effective nav always
// re-selects the correct tab/pane regardless of where we're coming from.
async function applyStepNav(index, surface) {
  const step = contentSteps[index]
  if (!step) return
  const nav = step.nav

  // Must happen before waitForSelectorReady/moveNext() try to resolve this step's
  // element — the marker has to already exist, not get created reactively afterward.
  if (step.id === 'calendar-create-lesson') {
    startCalendarSlotTracking()
  } else {
    stopCalendarSlotTracking()
  }

  if (!nav?.keepModalsOpen) {
    closeLessonModals()
  } else if (index > 0) {
    // This step's own click depends on a modal the PRECEDING step opens (e.g. clicking
    // "Edit" inside the details modal) — true going forward, but not if we're jumping
    // straight here (Previous, or any multi-step move). Replay that step's nav first so
    // the prerequisite modal actually exists before we try to click inside it.
    await applyStepNav(index - 1, surface)
  }

  const effective = effectiveNavs[index] || {}

  if (surface === 'mobile' && effective.mobileRoute) {
    const targetTabId = MOBILE_NAV_ID_BY_ROUTE[effective.mobileRoute]
    const iconEl = targetTabId && document.querySelector(`[data-tour="nav-${targetTabId}"]`)
    iconEl?.classList.add('tour-nav-pulse')
    setTimeout(() => iconEl?.classList.remove('tour-nav-pulse'), 600)
    if (router.currentRoute.value.path !== effective.mobileRoute) {
      await router.replace(effective.mobileRoute)
    }
  }

  if (surface === 'desktop' && effective.desktopPane) {
    window.dispatchEvent(new CustomEvent('tour:set-pane', { detail: effective.desktopPane }))
  }

  // Usually one event, but the Set Availability step needs TWO fired before its click
  // (expand the availability editor, then open the user menu) — accept either shape
  // rather than adding a separate parallel nav field just for that one step.
  const dispatchEvents = Array.isArray(nav?.dispatchEvent) ? nav.dispatchEvent : nav?.dispatchEvent ? [nav.dispatchEvent] : []
  dispatchEvents.forEach((name) => window.dispatchEvent(new CustomEvent(name)))

  // Every remaining simulateClick target (the lesson title, the details modal's Edit
  // button) sets EXPLICIT state on click — safe to "click" repeatedly, unlike a toggle.
  if (nav?.simulateClick) {
    // A step that also fired dispatchEvent above (e.g. open the user menu, then click
    // an item inside it) needs a beat before the click: Vue patches the DOM for that
    // dropdown on the next microtask, not synchronously within this call, so querying
    // for the click target immediately after dispatchEvent can miss it. Only steps that
    // combine both ever hit this wait — a bare simulateClick (target already present)
    // resolves it on the very first check.
    if (dispatchEvents.length) await waitForSelectorReady(nav.simulateClick, 500, 20)
    document.querySelector(nav.simulateClick)?.click()
  }
}

// Polls for a target element rather than trusting driver.js's own `waitForElement` +
// `skipMissingElement`: those fire BEFORE our async nav above has necessarily finished
// (a route change, a Vue re-render from a pane switch, a modal-opening click), and if
// driver.js decides the element is missing it silently skips the whole step — bypassing
// our onNextClick pipeline entirely, so the step after it never gets ITS nav applied
// either (e.g. a lesson that isn't editable had no Edit button to click, so the edit-
// scopes step's element never appeared, and driver.js jumped straight to the calendar
// step without switching tabs or closing the still-open details modal). Waiting HERE,
// before we ever call moveNext()/movePrevious() ourselves, means driver.js only ever
// tries to highlight a step once its element genuinely exists (or we've given up and
// it renders as a centered popover instead — never a silent skip).
async function waitForSelectorReady(selector, timeoutMs = 1500, intervalMs = 40) {
  if (!selector) return
  const start = Date.now()
  while (Date.now() - start < timeoutMs) {
    if (document.querySelector(selector)) return
    await new Promise((r) => setTimeout(r, intervalMs))
  }
}

function buildSteps(surface, includeUniversal, firstName, whatsNew) {
  contentSteps = whatsNew
    // A dedicated, short tour of just what changed for TOUR_VERSION (see tourSteps.js's
    // own comment on whatsNewV3Steps) — NOT a full replay with a different intro slide.
    // includeUniversal/desktopOnlySteps don't apply here; every entry in this list is
    // fully self-sufficient (its own nav), and its own closing slide is the last entry.
    ? whatsNewV3Steps
    : [
        ...(includeUniversal ? universalSteps : []),
        // Resize steps only exist on desktop, and come BEFORE the account-related steps
        // there (per design) — on mobile there's nothing to insert, so accountSteps just
        // follows universalSteps directly, same as before this split.
        ...(surface === 'desktop' ? desktopOnlySteps : []),
        ...(includeUniversal ? accountSteps : []),
        buildClosingStep(),
      ]
  // Carry the most recent explicit mobileRoute/desktopPane forward across steps that
  // don't set their own (they assume whatever the last step selected is still showing).
  let lastMobileRoute = null
  let lastDesktopPane = null
  effectiveNavs = contentSteps.map((s) => {
    if (s.nav?.mobileRoute) lastMobileRoute = s.nav.mobileRoute
    if (s.nav?.desktopPane) lastDesktopPane = s.nav.desktopPane
    return { mobileRoute: lastMobileRoute, desktopPane: lastDesktopPane }
  })
  const driveSteps = contentSteps.map((s) => {
    const selector = bySurface(s.selector, surface)
    const placement = bySurface(s.placement, surface)
    const ringSelector = bySurface(s.ringSelector, surface)
    const advanceOnClick = bySurface(s.advanceOnClick, surface)
    const emphasizeMain = shouldEmphasizeMain(s, surface)
    // No resolved selector for this surface → no `element` key at all, same as the
    // welcome/closing steps: driver.js renders those as a centered floating popover.
    return {
      ...(selector ? { element: () => document.querySelector(selector) } : {}),
      popover: { title: s.title, description: s.description, side: placement?.side, align: placement?.align },
      // No skipMissingElement/waitForElement here — we resolve readiness ourselves in
      // onNextClick/onPrevClick (see waitForSelectorReady) before ever calling
      // moveNext()/movePrevious(), so driver.js never has to guess.
      onHighlightStarted: (element) => {
        if (emphasizeMain) startEmphasis('main', element)
        // ringSelector is usually a single selector, but the closing step needs TWO
        // independent rings (the whole dropdown + the trigger button) — accept either
        // shape rather than adding a separate parallel field just for that one step.
        const ringSelectors = Array.isArray(ringSelector) ? ringSelector : ringSelector ? [ringSelector] : []
        ringSelectors.forEach((sel, i) => startEmphasis(`secondary-${i}`, document.querySelector(sel)))
        if (s.lowDim) setOverlayOpacity(LOW_DIM_OPACITY)
        if (advanceOnClick) {
          const target = document.querySelector(advanceOnClick)
          const handler = () => advanceToNextStep(surface)
          target?.addEventListener('click', handler)
          advanceClickCleanup = () => target?.removeEventListener('click', handler)
        }
        if (s.armUntilFirstClick) {
          document.body.classList.add('tour-calendar-armed')
          // Capture phase, same reasoning as onGridClick's own capture-listener docs
          // elsewhere — this only needs to SEE the click (to know when to disarm), not
          // intercept it, so it runs alongside the real Vue handlers rather than before
          // them. Disarming happens AFTER this click is already dispatched, so it never
          // blocks the very click that triggered it — only ones after.
          const handler = (e) => {
            if (e.target.closest('[data-tour="calendar-grid-choice"]')) {
              document.body.classList.remove('tour-calendar-armed')
            }
          }
          document.addEventListener('click', handler, true)
          armClickCleanup = () => {
            document.removeEventListener('click', handler, true)
            document.body.classList.remove('tour-calendar-armed')
          }
        }
      },
      // driver.js auto-focuses the popover's Close (X) button by default (it's the
      // first focusable element in its DOM) — meaning Space/Enter would activate Close,
      // not Next, unless we steal focus back. Must happen in onHighlighted, not
      // onHighlightStarted: the popover (and driver's own auto-focus) isn't rendered
      // yet when onHighlightStarted fires — for the default animated transition it's
      // deferred to roughly the midpoint of the transition — but onHighlighted fires
      // only once the transition (and thus the popover render + auto-focus) is fully
      // done, so refocusing here always runs last. Same button element serves both
      // "Next" and "Done" (driver.js just adds a class, doesn't swap elements), so one
      // selector covers every step including the last.
      onHighlighted: () => {
        document.querySelector('.driver-popover-next-btn')?.focus()
      },
      onDeselected: () => {
        stopEmphasis('main')
        for (const key of [...emphasisRings.keys()]) {
          if (key.startsWith('secondary-')) stopEmphasis(key)
        }
        if (s.lowDim) setOverlayOpacity(OVERLAY_OPACITY[surface])
        if (advanceClickCleanup) {
          advanceClickCleanup()
          advanceClickCleanup = null
        }
        if (armClickCleanup) {
          armClickCleanup()
          armClickCleanup = null
        }
      },
    }
  })
  // Same Next/Done refocus as every other step (see the driveSteps mapping above) —
  // buildWelcomeStep's own object skips that mapping since it has no selector/emphasize
  // concerns, but it still needs Space/Enter to advance rather than hit driver.js's
  // auto-focused Close button.
  const welcomeStep = {
    ...(whatsNew ? buildWhatsNewStep(firstName) : buildWelcomeStep(firstName)),
    onHighlighted: () => {
      document.querySelector('.driver-popover-next-btn')?.focus()
    },
  }
  return [welcomeStep, ...driveSteps]
}

async function markComplete(surface) {
  try {
    const res = await fetch('/api/auth/tour', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(surface === 'desktop' ? { desktop_completed: true } : { mobile_completed: true }),
    })
    if (res.ok) {
      const user = await res.json()
      localStorage.setItem('user', JSON.stringify(user))
    }
  } catch (e) {
    console.error('Failed to record tour completion', e)
  }
}

// Shared by onNextClick/onPrevClick: `combinedIndex` is the target step's index in the
// FULL driver.js array (welcome is index 0, contentSteps[i] is combined index i+1).
async function goToIndex(combinedIndex, surface) {
  const contentIndex = combinedIndex - 1
  if (contentIndex < 0 || !contentSteps[contentIndex]) return
  await applyStepNav(contentIndex, surface)
  await waitForSelectorReady(bySurface(contentSteps[contentIndex].selector, surface))
}

// Same logic as the popover's own Next button (see onNextClick below) — factored out so
// a step's `advanceOnClick` target (see tourSteps.js) can trigger it too, for steps
// where the real element is genuinely clickable and clicking it IS the natural way to
// move on (e.g. the lesson Edit button, which also opens the very modal the next step
// is about).
async function advanceToNextStep(surface) {
  if (!driverObj) return
  await goToIndex((driverObj.getActiveIndex() ?? 0) + 1, surface)
  driverObj.moveNext()
}

function startTour(surface, { includeUniversal = true, firstName = null, whatsNew = false } = {}) {
  if (driverObj) driverObj.destroy()
  ensureTourStyles()
  currentSurface = surface
  const steps = buildSteps(surface, includeUniversal, firstName, whatsNew)

  driverObj = driver({
    allowClose: true,
    // A tap/swipe/scroll outside the popover should never dismiss the tour — only an
    // explicit X, Escape, or reaching the end should. Default overlayClickBehavior
    // would close on an outside click; a no-op hook makes outside interaction totally
    // inert as far as the TOUR is concerned (see the pointer-events exemptions below
    // for actually letting those taps/scrolls reach the real page).
    overlayClickBehavior: () => {},
    // Letting the page/calendar scroll while the tour is active — driver.js's own
    // default locks scrolling everywhere except the active element's own subtree.
    allowScroll: true,
    // Mobile relies on the emphasis ring + popover to point at things, rather than a
    // full-screen dark overlay — most mobile content fills nearly the whole viewport
    // anyway, so dimming it "hides" more than it highlights. Desktop keeps normal dimming.
    overlayOpacity: OVERLAY_OPACITY[surface],
    stagePadding: 6,
    // No "N of M" indicator — reads as a chore/checklist rather than a quick look, and
    // doesn't hold up well across two differently-sized tours (full vs. whatsNew).
    showProgress: false,
    popoverClass: 'bma-tour-popover',
    steps,
    // Config-level hooks fire for every Next/Previous click, uniformly across all
    // steps — driver.js's own supported pattern for driving async/multi-route tours:
    // override the click hook, do our own nav, then call moveNext()/movePrevious()
    // ourselves (there's no built-in multi-page-tour feature).
    onNextClick: async () => {
      await advanceToNextStep(surface)
    },
    onPrevClick: async (element, step, opts) => {
      await goToIndex((opts.driver.getActiveIndex() ?? 0) - 1, surface)
      opts.driver.movePrevious()
    },
    onDoneClick: () => {
      // The closing step opened the user menu to point at Help — leaving it open, or
      // the user sitting on whatever tab they last saw a step on, isn't a great landing
      // spot once the tour is actually done. Close the menu and return to Attendance.
      window.dispatchEvent(new CustomEvent('tour:close-user-menu'))
      if (surface === 'mobile') {
        if (router.currentRoute.value.path !== '/') router.replace('/')
      } else {
        window.dispatchEvent(new CustomEvent('tour:set-pane', { detail: { sidebarTab: 'attendance' } }))
      }
      markComplete(currentSurface)
      driverObj.destroy()
    },
    onDestroyed: () => {
      // Records the version as seen on ANY exit — Done, the X button, or Escape — not
      // just genuine completion. An early close still means the user has seen this
      // version's tour; without this, closing early left tour_version_seen unset, so
      // maybeStartFirstRunTour (App.vue) kept re-triggering the same "what's new" tour
      // on every refresh until the user clicked all the way through to Done.
      localStorage.setItem(TOUR_VERSION_KEY, String(TOUR_VERSION))
      closeLessonModals()
      stopAllEmphasis()
      stopCalendarSlotTracking()
      if (advanceClickCleanup) {
        advanceClickCleanup()
        advanceClickCleanup = null
      }
      if (armClickCleanup) {
        armClickCleanup()
        armClickCleanup = null
      }
      tourActive.value = false
      driverObj = null
      currentSurface = null
    },
  })

  tourActive.value = true
  driverObj.drive()
}

// Called from a watch(isWide, ...) in App.vue. If the live surface no longer matches
// the surface the active tour was built for, destroy cleanly — via onDestroyed only,
// never onDoneClick, so a mid-tour resize never falsely records completion.
function abortIfSurfaceMismatch(isWideVal) {
  if (!tourActive.value || !driverObj) return
  if (toSurface(isWideVal) !== currentSurface) {
    driverObj.destroy()
  }
}

export function useTour(routerInstance) {
  if (routerInstance) router = routerInstance
  return { tourActive, startTour, abortIfSurfaceMismatch }
}
